import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import AccordionContext from './context/accordion-context';
import noop from '../utils/noop';

const defaultOpenedItems = [];

function Accordion({
  allowMultipleItemsOpened = true,
  component: Component = 'div',
  openedAccordionItems = defaultOpenedItems,
  shouldPreventAnimation = false,
  shouldPreventInitialAnimation = false,
  onMount = noop,
  children,
  ...otherProps
}) {
  const [openedItems, setOpenedItems] = useState([]);
  const componentRef = useRef(null);
  const isRendered = componentRef.current;

  const getOpenedItems = useCallback(
    (items) => (allowMultipleItemsOpened ? items : items.slice(-1)),
    [allowMultipleItemsOpened]
  );

  const isInitialAnimationPrevented = useRef(shouldPreventInitialAnimation);

  useEffect(() => {
    setOpenedItems(getOpenedItems(openedAccordionItems));
  }, [openedAccordionItems, allowMultipleItemsOpened, getOpenedItems]);

  useEffect(() => {
    if (isRendered) {
      onMount();
      if (shouldPreventInitialAnimation) {
        isInitialAnimationPrevented.current = false;
      }
    }
  }, [isRendered, onMount, shouldPreventInitialAnimation]);

  const toggleItemOpen = useCallback(
    (itemId) => {
      const newOpenedItems = openedItems.includes(itemId)
        ? openedItems.filter((openedItem) => openedItem !== itemId)
        : [...openedItems, itemId];
      setOpenedItems(getOpenedItems(newOpenedItems));
    },
    [openedItems, setOpenedItems, getOpenedItems]
  );

  return (
    <AccordionContext.Provider
      value={{
        openedItems,
        toggleItemOpen,
        shouldPreventAnimation: isInitialAnimationPrevented.current || shouldPreventAnimation,
      }}
    >
      <Component {...otherProps} ref={componentRef}>
        {children}
      </Component>
    </AccordionContext.Provider>
  );
}

Accordion.propTypes = {
  allowMultipleItemsOpened: PropTypes.bool,
  openedAccordionItems: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  component: PropTypes.elementType,
  children: PropTypes.node.isRequired,
  shouldPreventAnimation: PropTypes.bool,
  shouldPreventInitialAnimation: PropTypes.bool,
  onMount: PropTypes.func,
};

export default Accordion;
